'use client'

import { useState, useEffect, Suspense, useRef } from 'react'
import { useRouter, useSearchParams } from 'next/navigation'
import { signIn } from 'next-auth/react'
import { useTheme } from 'next-themes'
import { useForm } from 'react-hook-form'
import { zodResolver } from '@hookform/resolvers/zod'
import { z } from 'zod'
import Image from 'next/image'
import { Loader2, Eye, EyeOff, CalendarCheck, Users, Sparkles } from 'lucide-react'
import { Button } from '@/components/ui/button'
import { Input } from '@/components/ui/input'
import { Label } from '@/components/ui/label'
import { Alert, AlertDescription } from '@/components/ui/alert'

const loginSchema = z.object({
  email: z.string().email('Email inválido'),
  password: z.string().min(6, 'Senha deve ter no mínimo 6 caracteres'),
})

type LoginForm = z.infer<typeof loginSchema>

// ─── Particle Canvas ────────────────────────────────────────────────
function ParticleCanvas({ isDark }: { isDark: boolean }) {
  const canvasRef = useRef<HTMLCanvasElement>(null)

  useEffect(() => {
    const canvas = canvasRef.current
    if (!canvas) return

    const ctx = canvas.getContext('2d')
    if (!ctx) return

    let animationId: number

    const resize = () => {
      canvas.width = window.innerWidth
      canvas.height = window.innerHeight
    }
    resize()
    window.addEventListener('resize', resize)

    const particles: { x: number; y: number; size: number; opacity: number; speedX: number; speedY: number }[] = []
    for (let i = 0; i < 60; i++) {
      particles.push({
        x: Math.random() * canvas.width,
        y: Math.random() * canvas.height,
        size: 2 + Math.random() * 4,
        opacity: 0.1 + Math.random() * 0.35,
        speedX: (Math.random() - 0.5) * 0.4,
        speedY: (Math.random() - 0.5) * 0.4,
      })
    }

    const animate = () => {
      ctx.clearRect(0, 0, canvas.width, canvas.height)
      for (const p of particles) {
        p.x += p.speedX
        p.y += p.speedY
        if (p.x < 0) p.x = canvas.width
        if (p.x > canvas.width) p.x = 0
        if (p.y < 0) p.y = canvas.height
        if (p.y > canvas.height) p.y = 0

        ctx.beginPath()
        ctx.arc(p.x, p.y, p.size, 0, Math.PI * 2)
        ctx.fillStyle = isDark
          ? `rgba(56, 189, 248, ${p.opacity * 0.6})`
          : `rgba(46, 134, 171, ${p.opacity})`
        ctx.fill()
      }
      animationId = requestAnimationFrame(animate)
    }
    animate()

    return () => {
      window.removeEventListener('resize', resize)
      cancelAnimationFrame(animationId)
    }
  }, [isDark])

  return <canvas ref={canvasRef} className="fixed inset-0 z-0 pointer-events-none" />
}

// ─── Feature Carousel ───────────────────────────────────────────────
const features = [
  {
    icon: CalendarCheck,
    title: 'Seu Dia Organizado',
    description: 'Agenda, tarefas e atendimentos em um só lugar. Comece o dia sabendo exatamente o que fazer.',
  },
  {
    icon: Users,
    title: 'Trabalhe em Equipe',
    description: 'Informações compartilhadas em tempo real. Quando um avança, todos avançam.',
  },
  {
    icon: Sparkles,
    title: 'Resultados que Aparecem',
    description: 'Cada ação aqui dentro se transforma em resultado. Seu trabalho faz a diferença.',
  },
]

function FeatureCarousel({ isDark }: { isDark: boolean }) {
  const [current, setCurrent] = useState(0)
  const [direction, setDirection] = useState<'forward' | 'reverse'>('forward')

  useEffect(() => {
    const interval = setInterval(() => {
      setDirection('forward')
      setCurrent(prev => (prev + 1) % features.length)
    }, 5000)
    return () => clearInterval(interval)
  }, [])

  const feature = features[current]
  const Icon = feature.icon

  return (
    <div className="relative h-[180px] w-full">
      <div
        key={current}
        className={`absolute inset-0 ${direction === 'forward' ? 'animate-carouselSlideIn' : 'animate-carouselSlideInReverse'}`}
      >
        <div className={`rounded-2xl p-6 h-full border ${
          isDark
            ? 'bg-white/[0.06] border-white/10'
            : 'bg-white/80 border-[#e2eaf2]'
        }`}>
          <div className="flex items-start gap-4">
            <div className={`shrink-0 w-12 h-12 rounded-xl flex items-center justify-center ${
              isDark ? 'bg-sky-400/15' : 'bg-[#2E86AB]/10'
            }`}>
              <Icon className={`h-6 w-6 ${isDark ? 'text-sky-400' : 'text-[#2E86AB]'}`} />
            </div>
            <div className="min-w-0">
              <h3 className={`text-lg font-semibold mb-2 ${isDark ? 'text-white' : 'text-[#1a1a2e]'}`}>
                {feature.title}
              </h3>
              <p className={`text-sm leading-relaxed ${isDark ? 'text-gray-400' : 'text-[#6b7280]'}`}>
                {feature.description}
              </p>
            </div>
          </div>
        </div>
      </div>

      {/* Dots */}
      <div className="absolute -bottom-8 left-1/2 -translate-x-1/2 flex gap-2">
        {features.map((_, i) => (
          <button
            key={i}
            onClick={() => {
              setDirection(i > current ? 'forward' : 'reverse')
              setCurrent(i)
            }}
            className={`h-2 rounded-full transition-all duration-300 ${
              i === current
                ? `w-6 ${isDark ? 'bg-sky-400' : 'bg-[#2E86AB]'}`
                : `w-2 ${isDark ? 'bg-sky-400/30' : 'bg-[#2E86AB]/30'}`
            }`}
          />
        ))}
      </div>
    </div>
  )
}

// ─── Login Content ──────────────────────────────────────────────────
function LoginContent() {
  const router = useRouter()
  const searchParams = useSearchParams()
  const { resolvedTheme } = useTheme()
  const [mounted, setMounted] = useState(false)
  const [error, setError] = useState('')
  const [loading, setLoading] = useState(false)
  const [googleLoading, setGoogleLoading] = useState(false)
  const [showPassword, setShowPassword] = useState(false)

  useEffect(() => { setMounted(true) }, [])

  const isDark = mounted ? resolvedTheme === 'dark' : false

  useEffect(() => {
    const errorParam = searchParams.get('error')
    if (errorParam === 'AccessDenied') {
      setError('Acesso negado. Seu email não está cadastrado no sistema.')
    } else if (errorParam) {
      setError('Erro ao fazer login. Tente novamente.')
    }
  }, [searchParams])

  const {
    register,
    handleSubmit,
    formState: { errors },
  } = useForm<LoginForm>({
    resolver: zodResolver(loginSchema),
  })

  async function onSubmit(data: LoginForm) {
    setError('')
    setLoading(true)
    try {
      const result = await signIn('credentials', {
        email: data.email,
        password: data.password,
        redirect: false,
      })
      if (result?.error) {
        setError('Email ou senha incorretos')
      } else {
        router.push('/')
        router.refresh()
      }
    } catch {
      setError('Erro ao fazer login. Tente novamente.')
    } finally {
      setLoading(false)
    }
  }

  async function handleGoogleLogin() {
    setError('')
    setGoogleLoading(true)
    await signIn('google', { callbackUrl: '/' })
  }

  // Color tokens based on theme
  const accent = isDark ? 'sky-400' : '[#2E86AB]'

  return (
    <div className={`min-h-screen w-full relative overflow-hidden transition-colors duration-300 ${
      isDark
        ? 'bg-gradient-to-br from-gray-950 via-gray-900 to-gray-950'
        : 'bg-gradient-to-br from-white via-[#f0f7fb] to-[#e1eef6]'
    }`}>
      <ParticleCanvas isDark={isDark} />

      <div className="relative z-10 flex min-h-screen">
        {/* ── Hero Left Column ── */}
        <div className="hidden lg:flex lg:w-[60%] flex-col justify-center px-16 xl:px-24">
          <div className="animate-fadeIn max-w-lg">
            {/* Logo */}
            <div className="mb-10">
              <Image
                src="/images/logo-gkhub.png"
                alt="GK Hub"
                width={280}
                height={80}
                className={`h-auto w-[280px] ${isDark ? 'brightness-0 invert' : ''}`}
                priority
              />
            </div>

            {/* Title */}
            <h1 className={`text-4xl xl:text-5xl font-bold leading-tight mb-4 ${isDark ? 'text-white' : 'text-[#1a1a2e]'}`}>
              Aqui começa o<br />
              <span className={isDark ? 'text-sky-400' : 'text-[#2E86AB]'}>seu dia</span>
            </h1>
            <p className={`text-lg mb-12 ${isDark ? 'text-gray-400' : 'text-[#6b7280]'}`}>
              Organize, colabore e faça acontecer. Sua ferramenta do dia a dia, simples e poderosa.
            </p>

            {/* Feature Carousel */}
            <FeatureCarousel isDark={isDark} />
          </div>
        </div>

        {/* ── Login Right Column ── */}
        <div className="w-full lg:w-[40%] flex items-center justify-center p-6 sm:p-8">
          <div className="w-full max-w-md animate-fadeSlideUp">
            {/* Mobile logo */}
            <div className="lg:hidden text-center mb-8">
              <Image
                src="/images/logo-gkhub.png"
                alt="GK Hub"
                width={260}
                height={74}
                className={`h-auto w-[260px] mx-auto mb-3 ${isDark ? 'brightness-0 invert' : ''}`}
              />
            </div>

            {/* Login Card */}
            <div className={`backdrop-blur-xl rounded-2xl p-8 shadow-xl border ${
              isDark
                ? 'bg-white/[0.06] border-white/10 shadow-black/20'
                : 'bg-white/70 border-white/60 shadow-black/[0.03]'
            }`}>
              <div className="mb-6">
                <h2 className={`text-2xl font-bold ${isDark ? 'text-white' : 'text-[#1a1a2e]'}`}>
                  Bem-vindo de volta
                </h2>
                <p className={isDark ? 'text-gray-400 mt-1' : 'text-[#6b7280] mt-1'}>
                  Digite suas credenciais para acessar
                </p>
              </div>

              {/* Google Login */}
              <Button
                variant="outline"
                className={`w-full h-12 rounded-lg mb-4 ${
                  isDark
                    ? 'border-white/15 text-gray-200 hover:bg-white/10'
                    : 'border-[#d1d5db]/80 text-[#374151] hover:bg-[#f0f7fb]'
                }`}
                onClick={handleGoogleLogin}
                disabled={googleLoading || loading}
              >
                {googleLoading ? (
                  <Loader2 className="mr-2 h-4 w-4 animate-spin" />
                ) : (
                  <svg className="mr-2 h-4 w-4" viewBox="0 0 24 24">
                    <path d="M22.56 12.25c0-.78-.07-1.53-.2-2.25H12v4.26h5.92a5.06 5.06 0 0 1-2.2 3.32v2.77h3.57c2.08-1.92 3.28-4.74 3.28-8.1z" fill="#4285F4"/>
                    <path d="M12 23c2.97 0 5.46-.98 7.28-2.66l-3.57-2.77c-.98.66-2.23 1.06-3.71 1.06-2.86 0-5.29-1.93-6.16-4.53H2.18v2.84C3.99 20.53 7.7 23 12 23z" fill="#34A853"/>
                    <path d="M5.84 14.09c-.22-.66-.35-1.36-.35-2.09s.13-1.43.35-2.09V7.07H2.18C1.43 8.55 1 10.22 1 12s.43 3.45 1.18 4.93l2.85-2.22.81-.62z" fill="#FBBC05"/>
                    <path d="M12 5.38c1.62 0 3.06.56 4.21 1.64l3.15-3.15C17.45 2.09 14.97 1 12 1 7.7 1 3.99 3.47 2.18 7.07l3.66 2.84c.87-2.6 3.3-4.53 6.16-4.53z" fill="#EA4335"/>
                  </svg>
                )}
                {googleLoading ? 'Redirecionando...' : 'Continuar com Google'}
              </Button>

              {/* Divider */}
              <div className="relative my-5">
                <div className="absolute inset-0 flex items-center">
                  <span className={`w-full border-t ${isDark ? 'border-white/10' : 'border-[#d1d5db]/60'}`} />
                </div>
                <div className="relative flex justify-center text-xs uppercase">
                  <span className={`px-3 ${
                    isDark ? 'bg-gray-900/80 text-gray-500' : 'bg-white/70 text-[#9ca3af]'
                  }`}>ou</span>
                </div>
              </div>

              {/* Form */}
              <form onSubmit={handleSubmit(onSubmit)} className="space-y-4">
                <div className="space-y-2">
                  <Label htmlFor="email" className={isDark ? 'text-gray-300' : 'text-[#374151]'}>Email</Label>
                  <Input
                    id="email"
                    type="email"
                    placeholder="seu@email.com"
                    {...register('email')}
                    className={`h-12 focus-visible:ring-${accent} focus-visible:border-${accent} ${
                      isDark
                        ? 'border-white/15 placeholder:text-gray-500 bg-white/[0.06] text-white'
                        : 'border-[#d1d5db]/80 placeholder:text-[#9ca3af] bg-white/50'
                    }`}
                  />
                  {errors.email && (
                    <p className="text-sm text-destructive">{errors.email.message}</p>
                  )}
                </div>

                <div className="space-y-2">
                  <Label htmlFor="password" className={isDark ? 'text-gray-300' : 'text-[#374151]'}>Senha</Label>
                  <div className="relative">
                    <Input
                      id="password"
                      type={showPassword ? 'text' : 'password'}
                      placeholder="••••••••"
                      {...register('password')}
                      className={`h-12 pr-11 focus-visible:ring-${accent} focus-visible:border-${accent} ${
                        isDark
                          ? 'border-white/15 placeholder:text-gray-500 bg-white/[0.06] text-white'
                          : 'border-[#d1d5db]/80 placeholder:text-[#9ca3af] bg-white/50'
                      }`}
                    />
                    <button
                      type="button"
                      onClick={() => setShowPassword(!showPassword)}
                      className={`absolute right-3 top-1/2 -translate-y-1/2 transition-colors ${
                        isDark ? 'text-gray-500 hover:text-gray-300' : 'text-[#9ca3af] hover:text-[#6b7280]'
                      }`}
                    >
                      {showPassword ? <EyeOff className="h-5 w-5" /> : <Eye className="h-5 w-5" />}
                    </button>
                  </div>
                  {errors.password && (
                    <p className="text-sm text-destructive">{errors.password.message}</p>
                  )}
                </div>

                {error && (
                  <Alert variant="destructive">
                    <AlertDescription>{error}</AlertDescription>
                  </Alert>
                )}

                <Button
                  type="submit"
                  className={`w-full h-12 rounded-lg font-medium text-white ${
                    isDark
                      ? 'bg-sky-500 hover:bg-sky-400'
                      : 'bg-[#2E86AB] hover:bg-[#2E86AB]/90'
                  }`}
                  disabled={loading || googleLoading}
                >
                  {loading && <Loader2 className="mr-2 h-4 w-4 animate-spin" />}
                  {loading ? 'Entrando...' : 'Entrar'}
                </Button>
              </form>
            </div>

            {/* Footer */}
            <p className={`text-center text-xs mt-8 ${isDark ? 'text-gray-600' : 'text-[#9ca3af]'}`}>
              © {new Date().getFullYear()} GK Hub. Todos os direitos reservados.
            </p>
          </div>
        </div>
      </div>

      {/* CSS Animations */}
      <style jsx>{`
        @keyframes fadeSlideUp {
          from {
            opacity: 0;
            transform: translateY(20px);
          }
          to {
            opacity: 1;
            transform: translateY(0);
          }
        }
        @keyframes fadeIn {
          from { opacity: 0; }
          to { opacity: 1; }
        }
        @keyframes carouselSlideIn {
          from {
            opacity: 0;
            transform: translateX(30px);
          }
          to {
            opacity: 1;
            transform: translateX(0);
          }
        }
        @keyframes carouselSlideInReverse {
          from {
            opacity: 0;
            transform: translateX(-30px);
          }
          to {
            opacity: 1;
            transform: translateX(0);
          }
        }
        .animate-fadeSlideUp {
          animation: fadeSlideUp 0.6s ease-out;
        }
        .animate-fadeIn {
          animation: fadeIn 0.8s ease-out;
        }
        .animate-carouselSlideIn {
          animation: carouselSlideIn 0.5s ease-out;
        }
        .animate-carouselSlideInReverse {
          animation: carouselSlideInReverse 0.5s ease-out;
        }
      `}</style>
    </div>
  )
}

// ─── Fallback ───────────────────────────────────────────────────────
function LoginFallback() {
  return (
    <div className="min-h-screen w-full bg-gradient-to-br from-white via-[#f0f7fb] to-[#e1eef6] dark:from-gray-950 dark:via-gray-900 dark:to-gray-950 flex items-center justify-center">
      <div className="text-center">
        <div className="inline-flex items-center justify-center w-14 h-14 rounded-xl bg-[#2E86AB] dark:bg-sky-400 mb-4">
          <span className="text-white dark:text-gray-950 font-bold text-xl">GK</span>
        </div>
        <div className="mt-4">
          <Loader2 className="h-6 w-6 animate-spin text-[#2E86AB] dark:text-sky-400 mx-auto" />
        </div>
      </div>
    </div>
  )
}

// ─── Page ───────────────────────────────────────────────────────────
export default function LoginPage() {
  return (
    <Suspense fallback={<LoginFallback />}>
      <LoginContent />
    </Suspense>
  )
}
